import React, { createContext, useContext, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import clsx from 'clsx';

const MessageContext = createContext(null);

export const useMessage = () => useContext(MessageContext);

export const MessageProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);

  const removeMessage = useCallback((id) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  }, []);

  const showMessage = useCallback((type, content, duration = 2000) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);

    setMessages((prev) => {
      // Max 3 messages, remove oldest if needed
      const current = prev.length >= 3 ? prev.slice(1) : prev;
      return [...current, { id, type, content }];
    });

    setTimeout(() => {
      removeMessage(id);
    }, duration);
  }, [removeMessage]);

  return (
    <MessageContext.Provider value={{ showMessage }}>
      {children}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 pointer-events-none items-center">
        <AnimatePresence>
          {messages.map((msg) => (
            <MessageItem key={msg.id} message={msg} onRemove={removeMessage} />
          ))}
        </AnimatePresence>
      </div>
    </MessageContext.Provider>
  );
};

const MessageItem = ({ message, onRemove }) => {
  const { type, content } = message;

  const styles = {
    success: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-orange-50 border-orange-200 text-orange-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800', // Added info style
  };

  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-500" />,
    warning: <AlertTriangle className="w-5 h-5 text-orange-500" />,
    error: <XCircle className="w-5 h-5 text-red-500" />,
    info: <Info className="w-5 h-5 text-blue-500" />,
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
      layout
      className={clsx(
        'pointer-events-auto w-[90vw] max-w-sm p-4 rounded-lg shadow-lg border flex items-start gap-3',
        styles[type] || styles.success
      )}
    >
      <div className="shrink-0 mt-0.5">{icons[type]}</div>
      <div className="flex-1 text-sm font-medium break-words">{content}</div>
    </motion.div>
  );
};
